import type { Action, Computed } from 'easy-peasy';
import { action, computed } from 'easy-peasy';

/**
 * State of an auto save operation
 */
export type AutoSaveState =
    'initial' | 'unsaved' | 'saving' | 'saved' | 'error';

/**
 * Represents a single save operation tracked in the global store
 */
export interface SaveOperation {
    id: string;
    state: AutoSaveState;
    priority?: number; // For future: prioritize critical saves
}

/**
 * Model for global autosave coordination
 * Tracks all active save operations across the application
 */
export interface AutoSaveModel {
    // State
    /**
     * All currently active save operations, keyed by their unique ID
     */
    activeSaves: Record<string, SaveOperation>;

    /**
     * Timestamp of the last successful save operation
     */
    lastSavedAt: Date | null;

    // Actions
    /**
     * Register a new save operation to be tracked
     */
    registerSave: Action<AutoSaveModel, { id: string; priority?: number }>;

    /**
     * Update the state of a tracked save operation
     */
    updateState: Action<AutoSaveModel, { id: string; state: AutoSaveState }>;

    /**
     * Unregister a save operation (e.g., when component unmounts)
     */
    unregisterSave: Action<AutoSaveModel, string>;

    /**
     * Set the timestamp of the last successful save
     */
    setLastSaved: Action<AutoSaveModel, Date>;

    /**
     * Clear all tracked save operations
     */
    clearAll: Action<AutoSaveModel>;

    // Computed
    /**
     * Global save state aggregated from all active save operations
     * Priority: error > saving > unsaved > saved > initial
     */
    globalState: Computed<AutoSaveModel, AutoSaveState>;

    /**
     * Whether there are any pending changes across all operations
     */
    hasPendingChanges: Computed<AutoSaveModel, boolean>;

    /**
     * Count of operations in 'unsaved' state
     */
    pendingCount: Computed<AutoSaveModel, number>;

    /**
     * Count of operations in 'error' state
     */
    errorCount: Computed<AutoSaveModel, number>;

    /**
     * Count of operations in 'saving' state
     */
    savingCount: Computed<AutoSaveModel, number>;
}

export const autoSaveModel: AutoSaveModel = {
    // Initial state
    activeSaves: {},
    lastSavedAt: null,

    // Actions
    registerSave: action((state, payload) => {
        state.activeSaves[payload.id] = {
            id: payload.id,
            state: 'initial',
            priority: payload.priority ?? 0,
        };
    }),

    updateState: action((state, payload) => {
        if (state.activeSaves[payload.id]) {
            state.activeSaves[payload.id].state = payload.state;
            if (payload.state === 'saved') {
                state.lastSavedAt = new Date();
            }
        }
    }),

    unregisterSave: action((state, id) => {
        // Using delete is safe with Immer (easy-peasy uses Immer under the hood)
        // Immer converts this mutation into an immutable update
        delete state.activeSaves[id];
    }),

    setLastSaved: action((state, date) => {
        state.lastSavedAt = date;
    }),

    clearAll: action((state) => {
        state.activeSaves = {};
    }),

    // Computed properties
    globalState: computed((state) => {
        const operations = Object.values(state.activeSaves);

        if (operations.length === 0) {
            return 'initial';
        }

        // Filter out 'initial' operations - they haven't been touched yet
        const activeOperations = operations.filter(
            (op) => op.state !== 'initial',
        );

        // If no operations have been touched, we're still in initial state
        if (activeOperations.length === 0) {
            return 'initial';
        }

        // Check states in priority order
        if (activeOperations.some((op) => op.state === 'error')) {
            return 'error';
        }
        if (activeOperations.some((op) => op.state === 'saving')) {
            return 'saving';
        }
        if (activeOperations.some((op) => op.state === 'unsaved')) {
            return 'unsaved';
        }
        // All active operations are saved
        if (activeOperations.every((op) => op.state === 'saved')) {
            return 'saved';
        }

        return 'initial';
    }),

    hasPendingChanges: computed((state) => {
        return Object.values(state.activeSaves).some(
            (op) => op.state === 'unsaved' || op.state === 'saving',
        );
    }),

    pendingCount: computed((state) => {
        return Object.values(state.activeSaves).filter(
            (op) => op.state === 'unsaved',
        ).length;
    }),

    errorCount: computed((state) => {
        return Object.values(state.activeSaves).filter(
            (op) => op.state === 'error',
        ).length;
    }),

    savingCount: computed((state) => {
        return Object.values(state.activeSaves).filter(
            (op) => op.state === 'saving',
        ).length;
    }),
};

export default autoSaveModel;
